type NotificationLike = {
  id?: string | number;
  title?: string;
  message?: string;
  read?: boolean;
  is_read?: boolean;
  type?: string;
  related_entity_type?: string;
};

const buildSignatureStorageKey = (scope: string, userId: string | number) => `notification-alert-signatures:${scope}:${userId}`;

const normalizeSignaturePart = (value: unknown) => String(value || '').trim().toLocaleLowerCase('tr-TR');

const buildNotificationSignature = (item: NotificationLike) => [
  normalizeSignaturePart(item.type),
  normalizeSignaturePart(item.related_entity_type),
  normalizeSignaturePart((item as any).related_entity_id),
  normalizeSignaturePart(item.title),
  normalizeSignaturePart(item.message),
].join('|');

const buildStorageKey = (scope: string, userId: string | number) => `notification-alert-cache:${scope}:${userId}`;

const readKnownIds = (scope: string, userId: string | number) => {
  try {
    const raw = sessionStorage.getItem(buildStorageKey(scope, userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((item) => String(item)) : [];
  } catch {
    return [];
  }
};

const writeKnownIds = (scope: string, userId: string | number, ids: string[]) => {
  try {
    sessionStorage.setItem(buildStorageKey(scope, userId), JSON.stringify(ids.slice(-300)));
  } catch {
    // ignore storage errors
  }
};

const readKnownSignatures = (scope: string, userId: string | number) => {
  try {
    const raw = sessionStorage.getItem(buildSignatureStorageKey(scope, userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((item) => String(item)) : [];
  } catch {
    return [];
  }
};

const writeKnownSignatures = (scope: string, userId: string | number, signatures: string[]) => {
  try {
    sessionStorage.setItem(buildSignatureStorageKey(scope, userId), JSON.stringify(signatures.filter(Boolean).slice(-300)));
  } catch {
    // ignore storage errors
  }
};

export const primeNotificationAlertCache = (scope: string, userId: string | number, notifications: NotificationLike[]) => {
  const ids = notifications.map((item) => String(item.id || '')).filter(Boolean);
  const signatures = notifications.map(buildNotificationSignature).filter(Boolean);
  writeKnownIds(scope, userId, ids);
  writeKnownSignatures(scope, userId, signatures);
};

const playNotificationSound = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(660, context.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.22);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.24);
    oscillator.onended = () => {
      void context.close().catch(() => undefined);
    };
  } catch (error) {
    console.warn('Notification sound play failed:', error);
  }
};

export const announceNewNotifications = (scope: string, userId: string | number, notifications: NotificationLike[]) => {
  const knownIds = new Set(readKnownIds(scope, userId));
  const knownSignatures = new Set(readKnownSignatures(scope, userId));
  const currentIds = notifications.map((item) => String(item.id || '')).filter(Boolean);
  const currentSignatures = notifications.map(buildNotificationSignature).filter(Boolean);
  const fresh = notifications.filter((item) => {
    const id = String(item.id || '');
    const signature = buildNotificationSignature(item);
    const isUnread = !(item.read ?? item.is_read);
    return Boolean(id) && isUnread && !knownIds.has(id) && Boolean(signature) && !knownSignatures.has(signature);
  });

  writeKnownIds(scope, userId, [...knownIds, ...currentIds]);
  writeKnownSignatures(scope, userId, [...knownSignatures, ...currentSignatures]);

  if (fresh.length === 0) return [];

  return fresh;
};
